import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { FloatingPortal, autoUpdate, flip, shift, useDismiss, useFloating, useInteractions } from '@floating-ui/react';
import { useClickAway, useCopyToClipboard, useMediaQuery } from '@uidotdev/usehooks';
import {
  CirclePlus,
  Copy,
  Download,
  ExternalLink,
  Image,
  Laugh,
  Link,
  ListTodo,
  Megaphone,
  MessageSquare,
  Pencil,
  Reply,
  Trash2,
  Wallpaper,
  WandSparkles,
} from 'lucide-react';
import { useHotkeys } from 'react-hotkeys-hook';

import { apiPostAsUser } from '../../lib/api_v2';
import { beginChatBackgroundAction, canSetAttachmentAsChatBackground } from '../../lib/backgroundImage';
import { BREAKPOINT_SM } from '../../lib/breakpoints';
import { logError } from '../../lib/logger';
import { currentShadowId } from '../../lib/meta';
import { cn } from '../../lib/utils';
import { SCROLL_TO_WIDGET_EVENT } from '../../lib/widgetEvents';

import { KEYCHAIN, useRegisterWithKeyMaster } from '../../contexts/KeyMaster';
import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import { useListNavigation } from '../../hooks/useListNavigation';
import { useMessageWidgetActions } from '../../hooks/useMessageWidgetActions';
import useWorkspaceBackgroundImageMutation from '../../hooks/useWorkspaceBackgroundImageMutation';
import { GlassWrapper } from '../shared/GlassWrapper';
import { MenuButton } from '../shared/MenuButton';
import MenuDivider from '../shared/MenuDivider';

// largest variant for open/copy; root for download. Variants are written
// asynchronously by the upload pipeline, so any of them can be missing.
const getImageUrls = (attachment) => {
  if (!attachment?.public_url) return { downloadUrl: null, largeUrl: null };
  const largeUrl = attachment.variants?.large?.public_url || attachment.variants?.medium?.public_url || attachment.public_url;
  return { downloadUrl: attachment.public_url, largeUrl };
};

// The async clipboard API only reliably accepts image/png, so re-encode
// whatever the pipeline stored (jpeg/webp/...) through a canvas.
export const fetchAsPngBlob = async (url) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`fetch failed: ${response.status}`);
  const bitmap = await createImageBitmap(await response.blob());
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext('2d').drawImage(bitmap, 0, 0);
    return await new Promise((resolve, reject) => {
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('png encode failed'))), 'image/png');
    });
  } finally {
    bitmap.close();
  }
};

// Known template actions get a matching lucide glyph; anything else without a usable
// icon_url falls back to a generic lucide CirclePlus.
const WIDGET_ACTION_LUCIDE_ICONS = {
  save_as_announcement: Megaphone,
  add_to_todo_list: ListTodo,
};

const WidgetActionIcon = ({ iconUrl, actionId }) => {
  const [hasError, setHasError] = useState(false);
  const handleError = useCallback(() => setHasError(true), []);

  // Brand icons (e.g. Linear, GitHub) ship their own icon_url and keep it.
  if (iconUrl && !hasError) {
    return (
      <span className="flex size-4 shrink-0 items-center justify-center">
        <img
          src={iconUrl}
          alt=""
          className="max-h-4 max-w-4 object-contain"
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={handleError}
        />
      </span>
    );
  }

  const LucideIcon = WIDGET_ACTION_LUCIDE_ICONS[actionId] || CirclePlus;
  return (
    <span className="flex size-4 shrink-0 items-center justify-center">
      <LucideIcon size={12} aria-hidden="true" />
    </span>
  );
};

const ContextMenuError = ({ position, children, className }) => (
  <FloatingPortal>
    <div
      className={cn(
        'z-(--stack-context-menu) max-w-64 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 shadow-lg dark:border-red-900/40 dark:bg-red-950 dark:text-red-300',
        className,
      )}
      style={{ position: 'fixed', left: position.x, top: position.y }}
    >
      {children}
    </div>
  </FloatingPortal>
);

const ContextMenuPortal = ({
  isOpen,
  onClose,
  position,
  message,
  onAddReaction,
  clickedAttachment,
  onShowInChat,
  onBeforeRemix,
  remixChannelId,
  className,
}) => {
  const { workspaceId, profileId: currentProfileId, profile, isDirectMessage } = useRouteWorkspace();
  const { downloadUrl, largeUrl } = useMemo(() => getImageUrls(clickedAttachment), [clickedAttachment]);
  const clickawayRef = useClickAway(() => onClose());
  const [, copyToClipboard] = useCopyToClipboard();
  const [menuError, setMenuError] = useState(null);
  const canCreateReactions = !currentShadowId();
  const canLoadWidgetActions = Boolean(workspaceId && currentProfileId && canCreateReactions);
  const widgetActions = useMessageWidgetActions(workspaceId, { enabled: canLoadWidgetActions });
  const { saveExistingAttachment } = useWorkspaceBackgroundImageMutation(workspaceId);
  const canSetChatBackground = canSetAttachmentAsChatBackground({ attachment: clickedAttachment, profile, isDirectMessage });

  const { unlocked } = useRegisterWithKeyMaster(isOpen ? KEYCHAIN.POPOVER__CONTEXT_MENU : null);

  const isBreakpointSm = useMediaQuery(BREAKPOINT_SM);

  // Create a virtual element at the click/touch position
  const virtualElement = useRef(null);
  virtualElement.current = {
    getBoundingClientRect() {
      return {
        width: 0,
        height: 0,
        x: position.x,
        y: position.y,
        left: position.x,
        right: position.x,
        top: position.y,
        bottom: position.y,
      };
    },
  };

  const { refs, floatingStyles, context, update } = useFloating({
    open: isOpen,
    onOpenChange: onClose,
    placement: 'right-start',
    middleware: [
      flip({
        fallbackPlacements: ['right-start', 'left-start', 'bottom-start', 'top-start'],
      }),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  // Virtual elements need useLayoutEffect (to avoid flash at 0,0) and manual update() calls.
  // FloatingUI's autoUpdate uses ResizeObserver/MutationObserver on DOM nodes, but can't
  // detect when our virtual element's getBoundingClientRect() returns new values.
  useLayoutEffect(() => {
    refs.setReference(virtualElement.current);
    if (isOpen) update();
  }, [refs, isOpen, position.x, position.y, update]);

  const dismiss = useDismiss(context);
  const { getFloatingProps } = useInteractions([dismiss]);

  useEffect(() => {
    const handleToggleContextMenu = (e) => {
      if (!e.detail.isOpen) onClose();
    };

    if (isOpen) {
      window.addEventListener('output:close-context-menu', onClose);
      window.addEventListener('output:toggle-context-menu', handleToggleContextMenu);
    }

    return () => {
      window.removeEventListener('output:close-context-menu', onClose);
      window.removeEventListener('output:toggle-context-menu', handleToggleContextMenu);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!menuError) return;

    const timeout = setTimeout(() => setMenuError(null), 4000);
    return () => clearTimeout(timeout);
  }, [menuError]);

  const closeMenu = useCallback(() => {
    onClose();
  }, [onClose]);

  // Include the rendered row so edit mode preloads text/attachments without a
  // legacy-store round trip that feed-only messages cannot satisfy.
  const handleEdit = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent('output:edit-message', {
        detail: { messageId: message?.id, sortOrder: message?.sort_order, channelId: message?.channel_id, message },
      }),
    );
    closeMenu();
  }, [message, closeMenu]);

  // Carry the rendered row itself: the confirmation modal must keep working after this
  // virtualized row unmounts, and feed-only messages have no legacy row to re-fetch.
  const handleDeleteMessage = useCallback(() => {
    window.dispatchEvent(new CustomEvent('output:internal:messages:delete-message-modal', { detail: { messageId: message?.id, message } }));
    closeMenu();
  }, [message, closeMenu]);

  const handleAddReaction = useCallback(
    (e) => {
      // Pass button position so emojibar opens where this button was.
      // When triggered via keyboard (no event / no currentTarget), fall back to the menu's click position.
      let buttonPosition;
      if (e?.currentTarget) {
        const buttonRect = e.currentTarget.getBoundingClientRect();
        buttonPosition = {
          x: buttonRect.left + buttonRect.width / 2,
          y: buttonRect.top + buttonRect.height / 2,
        };
      } else {
        buttonPosition = { x: position.x, y: position.y };
      }
      closeMenu();
      if (onAddReaction) onAddReaction(buttonPosition);
    },
    [closeMenu, onAddReaction, position.x, position.y],
  );

  const handleCopyMessageText = useCallback(() => {
    navigator.clipboard.write([
      new ClipboardItem({
        ['text/plain']: new Blob([message?.text], { type: 'text/plain' }),
        ['text/html']: new Blob([message?.text], { type: 'text/html' }),
      }),
    ]);

    closeMenu();
  }, [message?.text, closeMenu]);

  // Carry the rendered row so the reply banner can name the author without a
  // legacy-store lookup that feed-only messages cannot satisfy.
  const handleReply = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent('output:reply-to-message', { detail: { messageId: message?.id, channelId: message?.channel_id, message } }),
    );
    closeMenu();
  }, [message, closeMenu]);

  const handleCopyImageUrl = useCallback(() => {
    if (!largeUrl) return;
    copyToClipboard(largeUrl);
    closeMenu();
  }, [largeUrl, closeMenu, copyToClipboard]);

  const handleCopyImage = useCallback(() => {
    if (!largeUrl) return;
    closeMenu();

    // Safari only allows clipboard writes inside the user gesture, so the
    // ClipboardItem must be created synchronously with a promise for the bytes.
    if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) {
      copyToClipboard(largeUrl);
      return;
    }

    navigator.clipboard.write([new ClipboardItem({ 'image/png': fetchAsPngBlob(largeUrl) })]).catch((err) => {
      logError('ContextMenuPortal', 'Failed to copy image, falling back to URL', { error: err });
      copyToClipboard(largeUrl);
    });
  }, [largeUrl, closeMenu, copyToClipboard]);

  const handleDownloadImage = useCallback(async () => {
    if (!downloadUrl) return;
    closeMenu();
    try {
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = clickedAttachment?.metadata?.file_name?.replace(/[/\\]/g, '_') || 'image';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (err) {
      logError('ContextMenuPortal', 'Failed to download image', { error: err });
    }
  }, [downloadUrl, closeMenu, clickedAttachment]);

  const handleShowInChat = useCallback(() => {
    closeMenu();
    onShowInChat?.();
  }, [closeMenu, onShowInChat]);

  const handleSetChatBackground = useCallback(async () => {
    const attachmentId = clickedAttachment?.id;
    if (!workspaceId || !attachmentId) return;

    const errorPosition = { x: position.x, y: position.y };
    closeMenu();
    const action = beginChatBackgroundAction(workspaceId, attachmentId);
    if (!action) return;

    try {
      setMenuError(null);
      await saveExistingAttachment(attachmentId);
    } catch (error) {
      logError('ContextMenuPortal', 'Failed to set chat background image', { error, attachmentId });
      if (action.isLatest()) setMenuError({ message: "Couldn't set chat background image", position: errorPosition });
    } finally {
      action.finish();
    }
  }, [clickedAttachment?.id, closeMenu, position.x, position.y, saveExistingAttachment, workspaceId]);

  const handleOpenImageInNewWindow = useCallback(() => {
    if (!largeUrl) return;
    window.open(largeUrl, '_blank');
    closeMenu();
  }, [largeUrl, closeMenu]);

  const remixDestinationId = remixChannelId || message?.channel_id;
  const canRemixImage = Boolean(
    currentProfileId &&
      canCreateReactions &&
      remixDestinationId &&
      clickedAttachment?.id &&
      clickedAttachment?.public_url &&
      (!message?.pseudotype || message.pseudotype === 'zombie') &&
      ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/heic'].includes(clickedAttachment?.metadata?.content_type),
  );

  const handleRemixImage = useCallback(() => {
    const channelId = remixDestinationId;
    if (!workspaceId || !channelId || !clickedAttachment?.id) return;

    closeMenu();
    onBeforeRemix?.();
    window.setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent('output:internal:messages:image-remix-modal', {
          detail: { workspaceId, channelId, attachment: clickedAttachment },
        }),
      );
    }, 0);
  }, [clickedAttachment, closeMenu, onBeforeRemix, remixDestinationId, workspaceId]);

  const isOwnMessage = message?.author_type === 'profile' && currentProfileId === message?.author_id;
  const canUseWidgetActions = Boolean(
    message?.id &&
      (!message?.pseudotype || message.pseudotype === 'zombie') &&
      canCreateReactions &&
      currentProfileId &&
      widgetActions.length > 0,
  );

  const handleWidgetMessageAction = useCallback(
    async (entry) => {
      setMenuError(null);
      closeMenu();

      try {
        const response = await apiPostAsUser(`/widgets/${entry.widgetId}/message_actions/${entry.actionId}/deliveries`, {
          message_ids: [message.id],
        });

        if (!response.ok) {
          let errorCode = null;
          try {
            const body = await response.json();
            errorCode = body?.error?.code || body?.error_code || null;
          } catch {
            // Response body is optional; the status is enough for user-facing fallback.
          }
          throw new Error(errorCode || `HTTP ${response.status}`);
        }

        window.dispatchEvent(new CustomEvent(SCROLL_TO_WIDGET_EVENT, { detail: { widgetId: entry.widgetId } }));
      } catch (error) {
        logError('ContextMenuPortal', 'Failed to deliver widget message action', {
          error,
          widgetId: entry.widgetId,
          actionId: entry.actionId,
          messageId: message.id,
        });
        setMenuError(`Couldn't send to ${entry.label}`);
      }
    },
    [closeMenu, message?.id],
  );

  // Build menu items as data so keyboard navigation can index them
  const menuItems = useMemo(() => {
    const items = [];

    if (message) {
      // Generic, constant actions
      items.push({ key: 'reply', label: 'Reply', icon: Reply, handler: handleReply });
      if (canCreateReactions) items.push({ key: 'reaction', label: 'Add reaction', icon: Laugh, handler: handleAddReaction });
      if (isBreakpointSm) items.push({ key: 'copy', label: 'Copy text', icon: Copy, handler: handleCopyMessageText });

      // Divider before media or own message sections
      if (clickedAttachment || isOwnMessage) {
        items.push({ key: 'divider-1', type: 'divider' });
      }
    }

    // Media-specific items (if clicked on an image)
    if (clickedAttachment) {
      if (canRemixImage) items.push({ key: 'remix-image', label: 'Remix', icon: WandSparkles, handler: handleRemixImage });
      if (onShowInChat) items.push({ key: 'show-in-chat', label: 'Show in Chat', icon: MessageSquare, handler: handleShowInChat });
      if (canSetChatBackground) {
        items.push({
          key: 'set-chat-background',
          label: 'Set as chat background image',
          icon: Wallpaper,
          handler: handleSetChatBackground,
        });
      }
      items.push({ key: 'copy-image', label: 'Copy image', icon: Image, handler: handleCopyImage });
      items.push({ key: 'download-image', label: 'Download original', icon: Download, handler: handleDownloadImage });
      items.push({ key: 'copy-image-url', label: 'Copy image URL', icon: Link, handler: handleCopyImageUrl });
      items.push({ key: 'open-image', label: 'Open image', icon: ExternalLink, handler: handleOpenImageInNewWindow });
      if (message && isOwnMessage) {
        items.push({ key: 'divider-2', type: 'divider' });
      }
    }

    if (message) {
      // Own message actions (edit/delete)
      if (isOwnMessage) items.push({ key: 'edit', label: 'Edit', icon: Pencil, handler: handleEdit });
      if (isOwnMessage) items.push({ key: 'delete', label: 'Delete', icon: Trash2, variant: 'danger', handler: handleDeleteMessage });
    }

    if (canUseWidgetActions) {
      if (items.length > 0) items.push({ key: 'widget-divider', type: 'divider' });
      widgetActions.forEach((entry) => {
        items.push({
          key: `widget-action:${entry.key}`,
          type: 'widget_action',
          label: entry.label,
          iconUrl: entry.iconUrl,
          actionId: entry.actionId,
          handler: () => handleWidgetMessageAction(entry),
        });
      });
    }

    return items;
  }, [
    isOwnMessage,
    canCreateReactions,
    canRemixImage,
    canUseWidgetActions,
    isBreakpointSm,
    message,
    clickedAttachment,
    canSetChatBackground,
    onShowInChat,
    widgetActions,
    handleEdit,
    handleShowInChat,
    handleSetChatBackground,
    handleReply,
    handleAddReaction,
    handleCopyMessageText,
    handleDeleteMessage,
    handleOpenImageInNewWindow,
    handleRemixImage,
    handleCopyImage,
    handleCopyImageUrl,
    handleDownloadImage,
    handleWidgetMessageAction,
  ]);

  const handleItemSelect = useCallback((item) => {
    if (item.type !== 'divider') {
      item.handler();
    }
  }, []);

  // Filter out dividers for keyboard navigation
  const navItems = useMemo(() => menuItems.filter((item) => item.type !== 'divider'), [menuItems]);

  // Map nav index back to menu index for highlighting
  const navIndexToMenuIndex = useMemo(() => {
    const map = {};
    let navIdx = 0;
    menuItems.forEach((item, menuIdx) => {
      if (item.type !== 'divider') {
        map[navIdx] = menuIdx;
        navIdx += 1;
      }
    });
    return map;
  }, [menuItems]);

  const {
    selectedIndex: selectedNavIndex,
    setSelectedIndex,
    createMouseEnterHandler,
    onMouseLeave,
    onKeyDown,
  } = useListNavigation({
    items: navItems,
    onSelect: handleItemSelect,
    wrap: true,
    initialIndex: 0,
  });

  const selectedMenuIndex = navIndexToMenuIndex[selectedNavIndex] ?? -1;
  const menuErrorMessage = menuError?.message || menuError;
  const menuErrorPosition = menuError?.position || position;

  // Reset selection when menu opens/closes
  useEffect(() => {
    setSelectedIndex(isOpen ? 0 : -1);
  }, [isOpen, setSelectedIndex]);

  // Keyboard: ESC to close
  useHotkeys(
    'esc',
    () => {
      if (unlocked()) onClose();
    },
    { enabled: isOpen, enableOnFormTags: true, preventDefault: true },
  );

  // Keyboard: arrow/enter navigation — gate with unlocked(), delegate to useListNavigation
  useHotkeys(
    'up,down,enter',
    (e) => {
      if (!unlocked()) return;
      onKeyDown({ event: e });
    },
    { enabled: isOpen, enableOnFormTags: true, preventDefault: true },
  );

  if (!isOpen) {
    return menuError ? (
      <ContextMenuError position={menuErrorPosition} className={className}>
        {menuErrorMessage}
      </ContextMenuError>
    ) : null;
  }

  return (
    <FloatingPortal>
      <GlassWrapper
        ref={(node) => {
          refs.setFloating(node);
          clickawayRef.current = node;
        }}
        style={floatingStyles}
        {...getFloatingProps()}
        className={cn('z-(--stack-context-menu) w-52 gap-0 whitespace-nowrap', className)}
        onMouseLeave={onMouseLeave}
      >
        {menuError && <div className="px-2.5 py-1.5 text-xs text-red-600 dark:text-red-400">{menuErrorMessage}</div>}
        {menuItems.map((item, i) => {
          if (item.type === 'divider') {
            return <MenuDivider key={item.key} />;
          }
          const navIndex = Object.values(navIndexToMenuIndex).indexOf(i);
          return (
            <MenuButton
              key={item.key}
              variant={item.variant}
              isActive={selectedMenuIndex === i}
              onClick={item.handler}
              onMouseMove={createMouseEnterHandler(navIndex)}
            >
              {item.type === 'widget_action' ? (
                <span className="flex min-w-0 items-center gap-2.5">
                  <WidgetActionIcon iconUrl={item.iconUrl} actionId={item.actionId} />
                  <span className="truncate">{item.label}</span>
                </span>
              ) : item.icon ? (
                <span className="flex items-center gap-2.5">
                  <item.icon size={12} aria-hidden="true" />
                  {item.label}
                </span>
              ) : (
                item.label
              )}
            </MenuButton>
          );
        })}
      </GlassWrapper>
    </FloatingPortal>
  );
};

export default ContextMenuPortal;
